import { defaultAuthorizationRequest } from '../../../constants/defaultAuthorizationRequest';
import { CustomDataAttrGtmClick } from '../../../features/google/customDataType';
import {
  headerModalMenuUrls,
  loggedInHeaderButtonUrls,
  loggedInHeaderModalMenuUrls,
} from '../../../features/url/headerUrl';
import {
  useAppState,
  useSetAppState,
} from '../../../stores/contexts/AppStateContext';
import { DefaultProps } from '../../../types/DefaultProps';
import { ButtonBase } from '../../Button/ButtonBase';
import { HeaderIconButton } from '../../Header/HeaderIconButton';
import { HeaderModalMenu } from '../../Header/HeaderModalMenu';
import { HeaderProfileButton } from '../../Header/HeaderProfileButton';
import { HeaderProps, HeaderButtonGroupProps } from '../../Header/HeaderProps';
import {
  FiXIcon,
  FcBriefcaseIcon,
  FcViewDetailsIcon,
  FcFaqIcon,
  FcFinePrintIcon,
  FcUnlockIcon,
  FcConferenceCallIcon,
  FiClockIcon,
  FiUserIcon,
  FiLogInIcon,
} from '../../Icon';
import { ServiceLogo } from '../../ServiceLogo';

import styles from './MenuModal.module.scss';
import { SisterSiteBlock } from './SisterSiteBlock';

import type { AppEnv } from '@/packages/nurse-senka-frontend-sdk';
import type { FC, HTMLAttributes } from 'react';

// eslint-disable-next-line max-lines-per-function
const NotLoggedInMenuModal: FC<HeaderButtonGroupProps> = ({
  appEnv,
  redirectUriAfterRegistration,
}) => {
  const modalMenuUrls = headerModalMenuUrls(appEnv);

  return (
    <>
      <div className={styles.register}>
        <div className={styles['register-li']}>
          <ButtonBase
            icon={FiUserIcon}
            color="primary"
            width="wide"
            link={
              redirectUriAfterRegistration
                ? `${modalMenuUrls.memberRegistration}?${redirectUriAfterRegistration}`
                : modalMenuUrls.memberRegistration
            }
            customDataAttrGtmClick="header-modal-menu-registration-button"
          >
            会員登録
          </ButtonBase>
        </div>
        <div className={styles['register-li']}>
          <ButtonBase
            icon={FiLogInIcon}
            color="secondary"
            width="wide"
            link={modalMenuUrls.memberRegistration}
            customDataAttrGtmClick="header-modal-menu-registration-button"
          >
            ログイン
          </ButtonBase>
        </div>
      </div>
      <HeaderModalMenu
        icon={FcFinePrintIcon}
        link={modalMenuUrls.medicalTerm}
        title="看護用語集"
        text="わからない略語・専門用語を調べよう"
        customDataAttrGtmClick="header-modal-menu-medical-term-button"
      />
      <HeaderModalMenu
        icon={FcFaqIcon}
        link={modalMenuUrls.survey}
        title="アンケート"
        text="ご意見募集中！"
        customDataAttrGtmClick="header-modal-menu-survey-button"
      />
      <HeaderModalMenu
        icon={FcBriefcaseIcon}
        link={modalMenuUrls.careerChangeSupport}
        title="転職サポート"
        text="看護師専門の求人紹介サービス"
        customDataAttrGtmClick="header-modal-menu-career-change-support-button"
      />
      <HeaderModalMenu
        icon={FcViewDetailsIcon}
        link={modalMenuUrls.recruit}
        title="求人検索"
        text="全国の看護師求人をさくっと検索"
        customDataAttrGtmClick="header-modal-menu-recruit-button"
      />
      <HeaderModalMenu
        icon={FcConferenceCallIcon}
        link={modalMenuUrls.jobNurseSenka}
        title="ナース専科 求人ナビ"
        text="姉妹サイトで看護師求人を探す"
        customDataAttrGtmClick="header-modal-menu-job-nurse-senka-button"
      />
      <SisterSiteBlock appEnv={appEnv} />
    </>
  );
};

// ログイン済みメニュー
// eslint-disable-next-line max-lines-per-function
const LoggedInMenuModal: FC<HeaderProps> = ({ appEnv, loggedInUser }) => {
  const modalMenuUrls = loggedInHeaderModalMenuUrls(appEnv);
  const urls = loggedInHeaderButtonUrls(appEnv);

  return (
    <>
      <HeaderProfileButton
        appEnv={appEnv}
        loggedInUser={loggedInUser}
        icon={FiClockIcon}
        link={urls.my}
        customDataAttrGtmClick="header-my-button"
      >
        マイページ
      </HeaderProfileButton>
      <HeaderModalMenu
        icon={FcFinePrintIcon}
        link={modalMenuUrls.medicalTerm}
        title="看護用語集"
        text="わからない略語・専門用語を調べよう"
        customDataAttrGtmClick="header-modal-menu-medical-term-button"
      />
      <HeaderModalMenu
        icon={FcFaqIcon}
        link={modalMenuUrls.survey}
        title="アンケート"
        text="ご意見募集中！"
        customDataAttrGtmClick="header-modal-menu-survey-button"
      />
      <HeaderModalMenu
        icon={FcBriefcaseIcon}
        link={modalMenuUrls.careerChangeSupport}
        title="転職サポート"
        text="看護師専門の求人紹介サービス"
        customDataAttrGtmClick="header-modal-menu-career-change-support-button"
      />
      <HeaderModalMenu
        icon={FcViewDetailsIcon}
        link={modalMenuUrls.recruit}
        title="求人検索"
        text="全国の看護師求人をさくっと検索"
        customDataAttrGtmClick="header-modal-menu-recruit-button"
      />
      <HeaderModalMenu
        icon={FcConferenceCallIcon}
        link={modalMenuUrls.jobNurseSenka}
        title="ナース専科 求人ナビ"
        text="姉妹サイトで看護師求人を探す"
        customDataAttrGtmClick="header-modal-menu-job-nurse-senka-button"
      />
      <HeaderModalMenu
        icon={FcUnlockIcon}
        link={modalMenuUrls.logout}
        title="ログアウト"
        text="ナース専科よりログアウトします"
        customDataAttrGtmClick="header-modal-menu-logout-button"
      />
    </>
  );
};

type ElementProps = Omit<HTMLAttributes<HTMLElement>, keyof Props>;

type Props = DefaultProps & {
  appEnv: AppEnv;
  customDataAttrGtmClick?: CustomDataAttrGtmClick;
};

const ModalAbove: FC<Props> = ({ appEnv, theme }) => {
  const setAppState = useSetAppState();
  const closeMenuModal = () => {
    setAppState({ isMenuModalShown: false });
  };

  return (
    <ul className={styles['block-inner-flex']}>
      <li className={styles['header-logo']}>
        <ServiceLogo appEnv={appEnv} theme={theme} />
        <span className={styles['header-logo-text']}>
          {theme?.logoTitle as string}
        </span>
      </li>
      <div>
        <button onClick={closeMenuModal}>
          <HeaderIconButton
            icon={FiXIcon}
            customDataAttrGtmClick="header-menu-close"
          >
            閉じる
          </HeaderIconButton>
        </button>
      </div>
    </ul>
  );
};
export const MenuModal: FC<HeaderProps & ElementProps> = ({
  theme,
  appEnv,
  loggedInUser,
  authorizationRequest = defaultAuthorizationRequest,
  redirectUriAfterRegistration,
}) => {
  const state = useAppState();

  return (
    <>
      {state.isMenuModalShown ? (
        <div className={styles.modal}>
          <ModalAbove appEnv={appEnv} theme={theme} />

          <div className={styles['menu-modal-inner']}>
            {loggedInUser ? (
              <LoggedInMenuModal
                appEnv={appEnv}
                loggedInUser={loggedInUser}
                authorizationRequest={authorizationRequest}
              />
            ) : (
              <NotLoggedInMenuModal
                appEnv={appEnv}
                authorizationRequest={authorizationRequest}
                redirectUriAfterRegistration={redirectUriAfterRegistration}
              />
            )}
          </div>
        </div>
      ) : (
        ''
      )}
    </>
  );
};
